import axios, { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { getApiLink } from '../api';
import { iTagShort__API, NewsArticleFilter, NewsArticleModel } from '../apiSchema';
import { Tag } from '../models/Tag';
import { useData } from '../hooks/useData';

const DEFAULT_TAKE = 25;

export async function getNewsArticlesByFilter(filter: NewsArticleFilter): Promise<NewsArticleModel[]> {
    const response: AxiosResponse<NewsArticleModel[]> = await axios.post(
        getApiLink('NewsArticle/GetNewsArticlesByFilter'),
        filter
    );
    return response.data;
}

async function getTagsAssociatedWithNewsArticles(): Promise<Tag[]> {
    const response: AxiosResponse<iTagShort__API[]> = await axios.get(getApiLink('Tag/GetTagsAssociatedWithNewsArticles'));

    return response.data.map((t) => new Tag({ apiModel: t }));
}

interface iuseNewsArticlesWithFiltersConfig {
    orderBy: string;
    sortOrder: string;
    searchTerm?: string;
    tags?: number[];
}

export default function useNewsArticlesWithFilters({
    orderBy,
    sortOrder,
    searchTerm = '',
    tags = []
}: iuseNewsArticlesWithFiltersConfig) {
    const [isLoading, setIsLoading] = useState(true);
    const [hasNextItems, setHasNextItems] = useState(true);
    const [storedData, setStoredData] = useState<NewsArticleModel[]>([]);
    const [requestData, setRequestData] = useState<NewsArticleFilter>({
        orderBy,
        sortOrder,
        skip: 0,
        searchTerm,
        ...(tags?.length && tags.length > 0 && { tags })
    });

    const { data: tagsAssociatedWithNewsArticles, isLoading: isTagsLoading } = useData([], getTagsAssociatedWithNewsArticles);

    const loadNextPage = useCallback(() => {
        setRequestData({
            ...requestData,
            ...(requestData.skip !== undefined && { skip: requestData.skip + DEFAULT_TAKE }),
            ...(requestData.tags?.length && requestData.tags.length > 0 && { tags: requestData.tags }),
            ...(requestData.searchTerm?.length && { searchTerm: requestData.searchTerm })
        });
    }, [requestData]);

    useEffect(() => {
        let isActual = true;
        setIsLoading(true);

        const filter: NewsArticleFilter = {
            orderBy: requestData.orderBy,
            sortOrder: requestData.sortOrder,
            skip: requestData.skip,
            searchTerm: requestData.searchTerm,
            tags: requestData.tags,
            take: DEFAULT_TAKE
        };

        getNewsArticlesByFilter(filter)
            .then((newData) => {
                if (isActual) {
                    setStoredData((prevData) => {
                        return requestData.skip === 0 ? newData : [...prevData, ...newData];
                    });
                    setHasNextItems(newData.length === DEFAULT_TAKE);
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (isActual) {
                    setIsLoading(false);
                }
            });

        return () => {
            isActual = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DEFAULT_TAKE, JSON.stringify(requestData)]);

    useEffect(() => {
        setIsLoading(true);
        setStoredData([]);
        setHasNextItems(true);
        setRequestData({
            orderBy,
            sortOrder,
            skip: 0,
            ...(searchTerm?.length && { searchTerm: searchTerm }),
            ...(tags?.length && { tags })
        });
    }, [orderBy, sortOrder, tags, searchTerm]);

    return {
        isLoading: isLoading || isTagsLoading,
        data: storedData,
        tagsAssociatedWithNewsArticles,
        hasNextItems,
        loadNextPage
    };
}
